<template>
  <div class="container-fluid p-3">
    <div class="d-flex">
      <div class="settingsmenu d-flex flex-column bg-white rounded-left shadow-sm mt-2 mb-2">
        <div>
          <div v-for="(nav, index) in navItems" :key="index">
            <router-link
              tag="div"
              class="menuitem"
              :class="{ active: $route.name === nav.key }"
              :to="{
                name: nav.key,
                params: { occId: $route.params.occId },
              }"
            >
              <i :class="nav.icon"></i>
              <div class="nav-item-title">{{ nav.name }}</div>

              <i v-if="nav.additionalIcon && displayFilledWarning" :class="nav.additionalIcon" :title="nav.additionalIconPopover" v-b-tooltip:hover></i>
            </router-link>
          </div>
        </div>
        <div v-if="displayComatChart">
          <div class="label text-center mt-4">COMAT Process</div>
          <div class="mt-4 d-flex cursor-pointer align-items-center justify-content-center attachment" v-viewer="vvieweroptions">
            <div class="attachment-thumbnail">
              <img class="img-fluid" :src="'/comat_flow.png'" :data-src="'/comat_flow.png'" />
            </div>
          </div>
        </div>

        <div class="flex-grow-1 d-flex flex-column justify-content-end align-items-center">
          <div
            class="font-weight-bold cursor-pointer text-2xl mb-4"
            v-b-tooltip.hover
            :title="copyEmailToSpecialOccurrenceTitle"
            @click="copyEmailToSpecialOccurrence"
          >
            <i class="far fa-at fa-lg"></i>
            E-Mail2SO
          </div>
        </div>
      </div>

      <div class="bg-lightgray rounded-sm flex-grow-1 p-4 pl-5 pt-4 shadow-sm settingscontainer shadow-sm">
        <div style="min-width: 800px; max-width: 1550px">
          <router-view></router-view>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import SpecialOccurrenceData from '@/components/SpecialOccurrence/Data/Data.vue';
  import RouteNames from '@/types/routeNames';
  import { BoardModule, BoardStore } from '@/store/board';
  import { SpecialOccurrenceAccessLevel, SpecialOccurrenceTypeDto } from '@/apiclient/apiclient_generated';
  import { systemSettingsClient } from '@/apiclient/opsboardapi';
  import PublicConstants from '@/types/publicConstants';
  import SpecialOccurrenceService from '@/services/specialOccurrence.service';

  @Component({
    components: {
      SpecialOccurrenceData,
    },
  })
  export default class SpecialOccurrence extends Vue {
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private boardStore: BoardModule = BoardStore;
    materialRequestIsEnabled = false;

    copyEmailToSpecialOccurrenceTitle = 'Click to copy email to Special Occurrence identifier';

    private vvieweroptions = {
      inline: false,
      button: false,
      navbar: false,
      title: false,
      toolbar: false,
      tooltip: false,
      movable: true,
      zoomable: true,
      rotatable: true,
      scalable: true,
      transition: true,
      fullscreen: true,
      keyboard: true,
      url: 'data-src',
    };

    get displayFilledWarning() {
      const occurrence = this.store.currentOccurrence;

      const displayWarning =
        SpecialOccurrenceService.isTechnicalOccurrence(occurrence) && !SpecialOccurrenceService.isTechnicalOccurrenceFulFilled(occurrence);

      return displayWarning;
    }
    private navMenu = [
      {
        key: RouteNames.specialOccurrence.root,
        name: 'Data',
        icon: 'far fa-fw fa-cog',
        additionalIcon: 'far fa-info-circle required-warn',
        additionalIconPopover: PublicConstants.fillMandatoryFields,
      },
      {
        key: RouteNames.specialOccurrence.attachments,
        name: 'Attachments',
        icon: 'far fa-fw fa-file',
      },
      {
        key: RouteNames.specialOccurrence.taskGeneration,
        name: 'Task Generation',
        icon: 'far fa-fw fa-check-square',
      },
      {
        key: RouteNames.specialOccurrence.completion,
        name: 'Completion',
        icon: 'far fa-fw fa-plus-square',
      },
    ];

    async mounted() {
      await this.boardStore.loadAllBoards();
      await this.store.loadData();
      this.materialRequestIsEnabled = await systemSettingsClient.getIsMaterialRequestEnabled();
    }

    get currentOccurrence() {
      return this.store.currentOccurrence;
    }

    get displayComatChart() {
      if (!this.currentOccurrence) return false;
      return this.currentOccurrence.type == SpecialOccurrenceTypeDto.Comat;
    }

    get navItems() {
      const occ = this.currentOccurrence;
      const menu = [...this.navMenu];

      switch (occ.type) {
        case SpecialOccurrenceTypeDto.TechnicalOccurrence:
          menu.splice(2, 0, {
            key: RouteNames.specialOccurrence.updates,
            name: 'Updates',
            icon: 'far fa-fw fa-file-alt',
          });
          if (this.materialRequestIsEnabled) {
            menu.splice(4, 0, {
              key: RouteNames.specialOccurrence.materialRequestLink,
              name: 'MaterialRequest',
              icon: 'far fa-fw fa-cogs',
            });
          }
      }

      if (this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Admin) {
        menu.push({
          key: RouteNames.specialOccurrence.accessLevels,
          name: 'Access',
          icon: 'far fa-fw fa-lock',
        });
      }

      return menu;
    }

    copyEmailToSpecialOccurrence() {
      const marker = '#SO:' + this.currentOccurrence.id + '#';
      navigator.clipboard.writeText(marker).then(
        () => {
          const originalTitle = this.copyEmailToSpecialOccurrenceTitle;
          this.copyEmailToSpecialOccurrenceTitle = 'Copied!';

          setTimeout(() => {
            this.copyEmailToSpecialOccurrenceTitle = originalTitle;
          }, 2000);
        },
        function (err) {
          console.log(err);
        }
      );
    }
  }
</script>

<style lang="scss" scoped>
  .attachment-thumbnail {
    background: #e5e5e5;
    border-radius: 5px;
    height: 110px;
    width: 160px;
    text-align: center;
    font-weight: bold;
    color: #5e6c84;
    margin-right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      image-rendering: crisp-edges;
    }
  }
</style>
